import { SetStateAction, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

export const useTimeoutState = <T,>(
  defaultState: T,
): [T, (action: SetStateAction<T>, opts?: { timeout: number }) => void] => {
  const [state, _setState] = useState<T>(defaultState)
  const [currentTimeoutId, setCurrentTimeoutId] = useState<NodeJS.Timeout | undefined>()

  const setState = useCallback(
    (action: SetStateAction<T>, opts?: { timeout: number }) => {
      if (currentTimeoutId != null) {
        clearTimeout(currentTimeoutId)
      }

      _setState(action)

      const id = setTimeout(() => _setState(defaultState), opts?.timeout)
      setCurrentTimeoutId(id)
    },
    [currentTimeoutId, defaultState],
  )

  return [state, setState]
}

export const useDebounce = (effect: any, dependencies: any, delay: number) => {
  const callback = useCallback(effect, dependencies)

  useLayoutEffect(() => {
    const timeout = setTimeout(callback, delay)
    return () => clearTimeout(timeout)
  }, [callback, delay])
}

export const useDebounces = (callback: (...args: any[]) => void, delay: number) => {
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  })

  let timer: any

  const naiveDebounce = (func: (...args: any[]) => void, delayMs: number, ...args: any[]) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func(...args)
    }, delayMs)
  }

  return useMemo(
    () =>
      (...args: any) =>
        naiveDebounce(callbackRef.current, delay, ...args),
    [delay],
  )
}
